import { ROLES } from '@/features/auth';
import { PREFIXES } from '@/features/search';
import { i18n } from '@/shared/i18n/i18n';

/**
 * SŁOWNICTWO PODSTAWIANE DO KAŻDEGO TŁUMACZENIA.
 *
 * ⭐ Odpowiedź na jedną klasę usterki: **tekst twierdzi coś, co program i tak wie lepiej**,
 * i przez to gnije, gdy tamta rzecz się zmieni. Zgłoszenie usera dotyczyło ról („wymieniasz
 * po nazwie konkretne role — jak dodamy nową, trzeba szukać tłumaczeń"), ale skan wszystkich
 * 611 kluczy pokazał, że to szersze: w prozie siedziały też **lista prefiksów omniboksa**,
 * **lista modyfikatorów relacji** i **minimalna długość hasła wpisana słownie**.
 *
 * ⚠ Zamiast tego, czego user słusznie nie chciał: **API nie dosyła niczego** do budowania
 * komunikatów. Wartości biorą się ze stałych, które i tak są w przeglądarce — i to z tych
 * samych, na których stoi ZACHOWANIE (`PREFIXES` czyta parser omniboksa). Dzięki temu
 * komunikat nie może się rozjechać z tym, co robi program: czyta to samo źródło.
 *
 * ⛔ **Minimum hasła tu NIE mieszka (od 2026-08-22).** Stało tu jako `MIN_PASSWORD_LENGTH = 8`
 * — kopia reguły serwera, a serwer wymagał 10: przy pierwszej aktywacji na produkcji klient
 * wpisywał 8 i 9 znaków i za każdym razem czytał „co najmniej 8". Zachowanie stoi na serwerze
 * (`Identity:MinimumPasswordLength`), więc formularze czytają je z `GET /api/auth/policy`
 * (`usePasswordPolicy`), a komunikat odmowy — z `args.minLength` w odpowiedzi. To ta sama
 * zasada co wyżej (tekst czyta źródło zachowania), tylko źródło jest po drugiej stronie drutu.
 *
 * Mechanizm to `interpolation.defaultVariables` z i18next — wstrzykiwane do **każdej**
 * interpolacji, więc żadne tłumaczenie nie potrzebuje osobnego podania wartości i nie ma
 * mapy „kod błędu → dane". Dokładnie to, o co prosił user: *„każde tłumaczenie przechodzi
 * przez metodę, która może coś podstawić w miejsce placeholderów"*.
 *
 * ⚠ Ten plik stoi w `app/`, nie w `shared/i18n/` — i to nie jest przypadek: składanie
 * słownictwa wymaga zajrzenia do `features/`, czego fundamentowi nie wolno (reguła granic
 * w `.oxlintrc.json`). Composition root jest w `app/`, tak jak host modułów na backendzie.
 */

/** Przecinki i „albo" przed ostatnim — polszczyzna nie znosi listy sklejonej przecinkami. */
function wylicz(pozycje: readonly string[], spojnik: string): string {
  if (pozycje.length <= 1) {
    return pozycje[0] ?? '';
  }

  return `${pozycje.slice(0, -1).join(', ')} ${spojnik} ${pozycje.at(-1)}`;
}

/**
 * Modyfikatory relacji w komendzie omniboksa.
 *
 * ⚠ **Jedyna pozycja tej listy, której front nie wywodzi z własnego kodu** — rozstrzyga ją
 * `CommandParser.cs` po stronie serwera (`["used-by"] = ("depends-on", true)` itd.). Trzymam
 * ją tutaj **jednym ciągiem zamiast dwóch zdań w dwóch przekładach**: to nie jest szczelne,
 * ale zmniejsza liczbę miejsc do poprawienia z czterech (pl + en × 2 komunikaty) do jednego.
 * ⭐ Gdyby doszedł trzeci typ relacji (spec 12A dopuszcza `implements` / `tests`), poprawka
 * jest tu, obok komentarza mówiącego, gdzie leży prawda.
 */
const RELATION_MODIFIERS = ['@depends-on', '@has', '@used-by', '@part-of', '@owner'] as const;

function zbuduj() {
  const t = i18n.t.bind(i18n);

  // Nazwy ról z JEDNEGO słownika. Wcześniej ta sama nazwa („administrator") była wpisana
  // w kilkunastu zdaniach — zmiana etykiety roli wymagałaby przejścia po nich wszystkich.
  const role = Object.fromEntries(
    Object.values(ROLES).map((rola) => [rola, t(`settings.account.role.${rola}`, { defaultValue: rola })]),
  );

  return {
    role,

    /** Aktywne prefiksy omniboksa — bez trybu domyślnego, który nie ma znaku. */
    prefixes: PREFIXES.map((wpis) => wpis.prefix)
      .filter((prefix) => prefix !== '')
      .join(' '),

    relations: wylicz(RELATION_MODIFIERS, t('common.word.or', { defaultValue: 'albo' })),
  };
}

/**
 * Wpina słownictwo w i18next i odświeża je po zmianie języka.
 *
 * ⚠ Odświeżenie jest konieczne, bo nazwy ról są **tłumaczone** — bez tego przełączenie na
 * angielski zostawiłoby „administrator" w środku angielskiego zdania. Objaw byłby przy tym
 * cichy: reszta komunikatu przetłumaczona, jedno słowo nie.
 */
export function registerVocabulary(): void {
  const zastosuj = () => {
    i18n.options.interpolation = { ...i18n.options.interpolation, defaultVariables: zbuduj() };
  };

  zastosuj();
  i18n.on('languageChanged', zastosuj);
}
